import { useEffect, useState } from 'react'
import { RefreshCw, Server, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router'

import { InstallerDownloadCards } from 'components/ServerEditions/InstallerDownloadCards'
import { ServerTokenDisplay } from 'components/ServerEditions/ServerTokenDisplay'
import useCurrentAccount from 'query/useCurrentAccount'
import { useServerDevices, useUpdateDevice } from 'query/useDevices'
import { useRegenerateServerToken } from 'query/useServerEditions'
import {
  Alert,
  Badge,
  Button,
  ConfirmNameModal,
  DataTable,
  EmptyState,
  FormCard,
  Icon,
  IconButton,
  LoadingIndicator,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageContent,
  PageHeader,
} from 'ui'
import { formatRelativeTime, isOlderThanHours } from 'utils/dates'
import { getPlatformDisplay } from 'utils/platforms'

const getHealth = lastSeen => {
  if (!lastSeen) return { label: 'Never seen', tone: 'gray' }
  if (isOlderThanHours(lastSeen, 24)) return { label: 'Stale', tone: 'amber' }
  return { label: 'Healthy', tone: 'green' }
}

const TrashConfirmModal = ({ id, name, onTrashSuccess }) => {
  const { mutateAsync, isPending } = useUpdateDevice(id)

  return (
    <ConfirmNameModal
      actionLabel="Trash"
      confirmationAction="trash this server edition"
      isPending={isPending}
      name={name}
      onConfirm={async () => {
        await mutateAsync({ status: 'trashed' })
        onTrashSuccess()
      }}
      title="Trash Server Edition"
      trigger={(
        <IconButton label={`Trash ${name}`} title="Trash Server Edition" variant="danger">
          <Icon as={Trash2} />
        </IconButton>
      )}
    >
      This frees an active instance immediately. The server can be activated again with the server token.
    </ConfirmNameModal>
  )
}

const initialPageState = {
  isRotateModalOpen: false,
  isRotatingToken: false,
  rotationError: '',
  rotationSuccess: '',
  serverToken: null,
}

export const ServerEditionsPageComponent = () => {
  const navigate = useNavigate()
  const { data: currentAccount = {}, setData: setCurrentAccount } = useCurrentAccount()
  const {
    data: serverEditions,
    invalidateQuery,
    isError,
    isLoading,
  } = useServerDevices()

  const [pageState, setPageState] = useState(initialPageState)

  useEffect(() => {
    if (isError) navigate('/users/sign_in')
  }, [isError, navigate])

  const { mutate: regenerateServerToken } = useRegenerateServerToken({
    onError: error => setPageState(state => ({
      ...state,
      isRotatingToken: false,
      rotationError: error.message || 'Unable to rotate the server token.',
    })),
    onSuccess: ({ serverToken }) => {
      if (!serverToken) {
        setPageState(state => ({ ...state, isRotatingToken: false, rotationError: 'Unable to rotate the server token.' }))
        return
      }

      setPageState(state => ({
        ...state,
        isRotateModalOpen: false,
        isRotatingToken: false,
        rotationError: '',
        rotationSuccess: 'Server token rotated.',
        serverToken,
      }))

      setCurrentAccount({ ...currentAccount, serverToken })
    },
  })

  const serverToken = pageState.serverToken || currentAccount.serverToken || currentAccount.server_token || ''
  const activeCount = serverEditions.length
  const limit = currentAccount.serverInstanceLimit
  const usagePercent = limit > 0 ? Math.min((activeCount / limit) * 100, 100) : 0

  const toggleRotateTokenModal = event => {
    event?.preventDefault?.()
    setPageState(state => ({ ...state, isRotateModalOpen: !state.isRotateModalOpen, isRotatingToken: false, rotationError: '' }))
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      primary: true,
      render: server => (
        <span className="tw:flex tw:items-center tw:gap-3 tw:font-semibold tw:text-foreground-strong">
          <Icon as={Server} className="tw:text-foreground-subtle" />
          {server.name || `Server Edition ${server.id}`}
        </span>
      ),
    },
    {
      key: 'platform',
      label: 'Platform',
      render: server => {
        const platform = getPlatformDisplay(server.platform)
        return (
          <span className="tw:flex tw:items-center tw:gap-2">
            <Icon as={platform.icon} />
            <span>
              <span className="tw:block">{platform.label}</span>
              {platform.detail && <span className="ds-caption tw:block">{platform.detail}</span>}
            </span>
          </span>
        )
      },
    },
    { key: 'lastIP', label: 'Last IP', render: server => server.lastIP || server.last_ip || 'Unknown' },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      render: server => {
        const value = server.lastSeen || server.last_seen
        return value ? formatRelativeTime(value) : 'Never seen'
      },
    },
    {
      key: 'health',
      label: 'Health',
      render: server => {
        const health = getHealth(server.lastSeen || server.last_seen)
        return <Badge dot={health.tone !== 'gray'} tone={health.tone}>{health.label}</Badge>
      },
    },
  ]

  return (
    <>
      <PageHeader
        helpHref="https://docs.expandrive.com/server-edition"
        helpLabel="Help — Server Edition"
        subtitle="Activate and monitor headless ExpanDrive server instances licensed under this team."
        title="Server Editions"
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : (
          <>
            <div className="tw:grid tw:grid-cols-[minmax(0,1.6fr)_minmax(280px,1fr)] tw:gap-6 tw:shell:grid-cols-1">
              <FormCard description="Use this token on the server licensing screen." title="Server Token">
                {pageState.rotationSuccess && <Alert color="success">{pageState.rotationSuccess}</Alert>}
                <ServerTokenDisplay
                  actions={(
                    <IconButton label="Rotate Server Token" onClick={toggleRotateTokenModal}>
                      <Icon as={RefreshCw} />
                    </IconButton>
                  )}
                  token={serverToken}
                />
                <p className="ds-helper tw:mt-4">Rotating the token invalidates the previous value for new activations.</p>
              </FormCard>

              <FormCard description="Server seats currently in use." title="Active Instances">
                <div className="tw:mt-2 tw:flex tw:items-baseline tw:gap-2">
                  <span className="tw:text-5xl tw:font-light tw:text-foreground-strong">{activeCount}</span>
                  {limit !== null && limit !== undefined && <span className="tw:text-2xl tw:text-foreground-subtle">/ {limit}</span>}
                </div>
                {limit !== null && limit !== undefined && (
                  <div className="tw:mt-4">
                    <div className="tw:h-2 tw:overflow-hidden tw:rounded-pill tw:border tw:border-strong tw:bg-surface-subtle">
                      <div
                        aria-label={`${activeCount} of ${limit} server editions active`}
                        aria-valuemax={limit}
                        aria-valuemin="0"
                        aria-valuenow={activeCount}
                        className={activeCount >= limit ? 'tw:h-full tw:bg-status-danger-solid tw:transition-[width] tw:duration-base tw:ease-out-quart' : 'tw:h-full tw:bg-status-success-solid tw:transition-[width] tw:duration-base tw:ease-out-quart'}
                        role="progressbar"
                        style={{ width: `${usagePercent}%` }}
                      />
                    </div>
                    <Badge className="tw:mt-4" dot tone={activeCount >= limit ? 'red' : 'green'}>
                      {Math.max(limit - activeCount, 0)} {Math.max(limit - activeCount, 0) === 1 ? 'seat' : 'seats'} available
                    </Badge>
                  </div>
                )}
              </FormCard>
            </div>

            <section className="tw:mt-8">
              <h2 className="ds-heading-3">Download Server Edition</h2>
              <p className="ds-helper tw:mt-1 tw:mb-5">Install the headless build on your host, then activate it with the token above.</p>
              <InstallerDownloadCards />
            </section>

            <section className="tw:mt-8">
              <h2 className="ds-heading-3">Registered Instances</h2>
              <p className="ds-helper tw:mt-1 tw:mb-5">Servers activated with this token.</p>
              <DataTable
                actions={server => (
                  <TrashConfirmModal
                    id={server.id}
                    name={server.name || `Server Edition ${server.id}`}
                    onTrashSuccess={invalidateQuery}
                  />
                )}
                columns={columns}
                empty={(
                  <EmptyState icon={<Icon as={Server} size={34} />} title="No Active Server Editions">
                    Download a build and activate it with your server token to see it here.
                  </EmptyState>
                )}
                rows={serverEditions}
              />
            </section>
          </>
        )}
      </PageContent>

      <Modal isOpen={pageState.isRotateModalOpen} toggle={toggleRotateTokenModal}>
        <ModalHeader>Rotate Server Token</ModalHeader>
        <ModalBody>
          <p className="tw:text-lg">Existing installs keep their activation keys. Update notes, installers, or automation for future installs with the new token.</p>
          {pageState.rotationError && <Alert className="tw:mt-4" color="danger">{pageState.rotationError}</Alert>}
        </ModalBody>
        <ModalFooter>
          <span className="tw:flex-1" />
          <Button onClick={toggleRotateTokenModal} variant="secondary">Cancel</Button>
          <Button
            disabled={pageState.isRotatingToken}
            onClick={() => {
              setPageState(state => ({ ...state, isRotatingToken: true, rotationError: '', rotationSuccess: '' }))
              regenerateServerToken()
            }}
            variant="danger-solid"
          >
            {pageState.isRotatingToken ? 'Rotating…' : 'Rotate Token Now'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ServerEditionsPageComponent
